import { useState, useMemo, useCallback, useEffect } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  DollarSign,
  Banknote,
  PiggyBank,
  Home,
  Car,
  Briefcase,
  CreditCard,
  Building2,
  Landmark,
  Gem,
  Download,
  Wallet,
  ArrowLeftRight,
  Users,
  Watch,
  Coins,
  Bitcoin,
  BarChart3,
  AlertTriangle,
  Info,
  X,
  GripVertical,
  Loader2,
  ShieldAlert,
  RefreshCw,
  Link2,
  Unlink,
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import { useCurrency } from '@/contexts';
import { useAccounts, ACCOUNT_GROUPS } from '@/hooks/useAccounts';
import { usePowensPendingConnections } from '@/hooks/usePowensPendingConnections';
import { usePowensConnect } from '@/hooks/usePowensConnect';
import { usePowensRefresh } from '@/hooks/usePowensRefresh';
import { useConvertedNetWorth } from '@/hooks/useConvertedNetWorth';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import {
  NET_WORTH_PERIOD_DAYS,
  NET_WORTH_PERIODS,
  useBalanceChart,
  type NetWorthPeriod,
} from '@/hooks/useBalanceChart';
import { useTransactions } from '@/hooks/useTransactions';
import { PageHeader } from '@/components/PageHeader';
import { TransferModal } from '@/components/TransferModal';
import { LinkBankAccountModal, isLinkableToBank } from '@/components/LinkBankAccountModal';
import { AccountSourceBadge } from '@/components/AccountSourceBadge';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { useToast } from '@/hooks/use-toast';
import { clearLinkIntent, saveLinkIntent } from '@/lib/powensLinkIntent';
import { exportStyledExcel } from '@/lib/exportExcel';
import { findBankByName, getBankLogoUrl } from '@/lib/banks';
import type { Database } from '@/lib/database.types';

type Account = Database['public']['Tables']['accounts']['Row'];

/**
 * Sign convention for display.
 *
 * Liabilities are stored as a positive magnitude and shown negative. Assets are
 * shown as stored, so a checking account in overdraft keeps its minus sign —
 * taking Math.abs here would print a debt of 341,75 as if it were savings.
 * Same rule as AccountCards.
 */
function displayBalance(account: Pick<Account, 'type' | 'balance'>): number {
  return account.type === 'liability' ? -Math.abs(account.balance) : account.balance;
}

function groupDisplayTotal(group: { type: string; total: number }): number {
  return group.type === 'liability' ? -Math.abs(group.total) : group.total;
}

const SUBTYPE_ICONS: Record<string, typeof DollarSign> = {
  checking: Banknote,
  savings: PiggyBank,
  cash: DollarSign,
  investment: TrendingUp,
  real_estate: Home,
  vehicle: Car,
  other_asset: Gem,
  watch: Watch,
  jewelry: Gem,
  precious_metal: Coins,
  crypto: Bitcoin,
  pea_stock: BarChart3,
  credit_card: CreditCard,
  mortgage: Building2,
  loan: Landmark,
  other_liability: Briefcase,
  joint_account: Users,
  money_market: Landmark,
};

const SUBTYPE_COLORS: Record<string, string> = {
  checking: 'from-blue-500 to-cyan-500',
  savings: 'from-green-500 to-emerald-500',
  cash: 'from-emerald-400 to-green-500',
  investment: 'from-purple-500 to-pink-500',
  real_estate: 'from-teal-500 to-green-500',
  vehicle: 'from-indigo-500 to-blue-500',
  other_asset: 'from-yellow-500 to-amber-400',
  watch: 'from-purple-500 to-violet-500',
  jewelry: 'from-purple-400 to-pink-500',
  precious_metal: 'from-yellow-500 to-amber-500',
  crypto: 'from-orange-500 to-orange-600',
  pea_stock: 'from-blue-500 to-blue-600',
  credit_card: 'from-red-500 to-pink-500',
  mortgage: 'from-rose-500 to-red-500',
  loan: 'from-rose-500 to-red-500',
  other_liability: 'from-gray-500 to-slate-500',
  joint_account: 'from-blue-500 to-indigo-500',
  money_market: 'from-teal-500 to-cyan-500',
};

const GROUP_DOT_COLORS: Record<string, string> = {
  bankAccounts: 'bg-blue-400',
  livrets: 'bg-emerald-400',
  cash: 'bg-green-400',
  investments: 'bg-purple-400',
  realEstate: 'bg-teal-400',
  vehicles: 'bg-indigo-400',
  otherAssets: 'bg-yellow-400',
  crypto: 'bg-orange-400',
  pea: 'bg-blue-400',
  creditCards: 'bg-red-400',
  mortgages: 'bg-rose-400',
  loans: 'bg-rose-400',
  otherLiabilities: 'bg-gray-400',
};

function SortableAccountWrapper({
  id,
  children,
}: {
  id: string;
  children: (props: { dragHandleProps: Record<string, unknown> }) => React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id,
    animateLayoutChanges: () => false,
  });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    position: 'relative',
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
}

export default function AccountsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { formatAmount, formatAmountInCurrency, formatCompact, isSecretMode, currency } =
    useCurrency();

  const {
    accounts,
    loading,
    loadAccounts,
    updateAccount,
    deleteAccount,
    toggleHidden,
    reorderAccounts,
    calculateStats,
  } = useAccounts();
  const { transactions, loadTransactions } = useTransactions();
  const { connections, pendingConnections } = usePowensPendingConnections(accounts);
  const { connect: connectPowens, isConnecting: isPowensConnecting } = usePowensConnect();
  const { refresh: refreshBanks, isRefreshing: isRefreshingBanks } = usePowensRefresh();
  const hasPowensAccounts = useMemo(
    () => accounts.some((account) => account.source === 'powens'),
    [accounts]
  );

  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transferFromAccountId, setTransferFromAccountId] = useState<string | undefined>(undefined);
  const [period, setPeriod] = useState<NetWorthPeriod>('1m');
  const [summaryView, setSummaryView] = useState<'totals' | 'percent'>('totals');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('clevia-collapsed-groups');
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });
  const [showHidden, setShowHidden] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const [archiveTarget, setArchiveTarget] = useState<Account | null>(null);
  const [linkTarget, setLinkTarget] = useState<Account | null>(null);
  // The bank connection the user is coming back from, by its Powens id —
  // 'newest' when the callback could not name it. Null while the flow has not
  // left clevia yet, which is what keeps any account list off the first screen.
  const [resumedConnection, setResumedConnection] = useState<number | 'newest' | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirm();
  const { toast } = useToast();

  // Coming back from the bank webview with ?link=<id>: reopen the link flow for
  // the manual account the user started from. The parameter is dropped at
  // once so a reload or a shared URL does not reopen it.
  const linkParam = searchParams.get('link');
  const connectionParam = searchParams.get('connection');
  useEffect(() => {
    if (!linkParam || loading) return;
    const target = accounts.find((a) => a.id === linkParam);
    if (target && isLinkableToBank(target)) {
      setLinkTarget(target);
      setResumedConnection(connectionParam ? Number(connectionParam) : 'newest');
    }
    setSearchParams(
      (params) => {
        params.delete('link');
        params.delete('connection');
        return params;
      },
      { replace: true }
    );
  }, [linkParam, connectionParam, loading, accounts, setSearchParams]);

  // Resolved from the connection list rather than stored, so a connection the
  // sync writes a moment after the user lands is picked up on its own.
  const linkConnectionId = useMemo(() => {
    if (resumedConnection === null) return null;
    if (resumedConnection === 'newest') return connections[0]?.id ?? null;
    return (
      connections.find((c) => Number(c.powens_connection_id) === resumedConnection)?.id ?? null
    );
  }, [resumedConnection, connections]);

  const linkBankAccounts = useMemo(
    () =>
      linkConnectionId
        ? accounts.filter(
            (a) => a.source === 'powens' && a.powens_connection_id === linkConnectionId
          )
        : [],
    [accounts, linkConnectionId]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const stats = useMemo(() => calculateStats(), [calculateStats]);
  const { data: convertedNetWorth } = useConvertedNetWorth();
  const { getRate } = useExchangeRates();
  const accountCurrencyMap = useMemo(
    () => new Map(accounts.map((a) => [a.id, (a as any).currency || 'EUR'])),
    [accounts]
  );
  const convertBalance = useCallback(
    (balance: number, accountId: string) => {
      const acctCurrency = accountCurrencyMap.get(accountId) || 'EUR';
      const rate = getRate(acctCurrency, currency) ?? 1;
      return balance * rate;
    },
    [accountCurrencyMap, getRate, currency]
  );
  const { data: chartData } = useBalanceChart(
    accounts,
    period === 'all' ? 'all' : NET_WORTH_PERIOD_DAYS[period],
    undefined,
    convertBalance
  );

  // Compute period variation from chart data
  const periodVariation = useMemo(() => {
    if (!chartData || chartData.length < 2) return null;
    const startValue = chartData[0].value;
    const endValue = chartData[chartData.length - 1].value;
    const diff = endValue - startValue;
    const pct = startValue !== 0 ? (diff / Math.abs(startValue)) * 100 : null;
    return { diff, pct };
  }, [chartData]);

  const archiveStats = useMemo(() => {
    if (!archiveTarget) return { transactions: 0, transfers: 0 };
    const id = archiveTarget.id;
    let txCount = 0;
    let transferCount = 0;
    for (const tx of transactions) {
      if (tx.account_id === id || tx.to_account_id === id) {
        if (tx.type === 'transfer') transferCount++;
        else txCount++;
      }
    }
    return { transactions: txCount, transfers: transferCount };
  }, [archiveTarget, transactions]);

  // Group accounts by type and subtype
  const groupedAccounts = useMemo(() => {
    const groups: {
      key: string;
      type: 'asset' | 'liability';
      label: string;
      accounts: Account[];
      total: number;
      change: number;
    }[] = [];

    const allGroups = [
      ...ACCOUNT_GROUPS.asset.map((g) => ({ ...g, type: 'asset' as const })),
      ...ACCOUNT_GROUPS.liability.map((g) => ({ ...g, type: 'liability' as const })),
    ];

    for (const group of allGroups) {
      const groupAccounts = accounts
        .filter(
          (a) =>
            a.type === group.type &&
            group.subtypes.includes(a.subtype as never) &&
            !(a.metadata as Record<string, unknown> | null)?.archived
        )
        .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));

      if (groupAccounts.length > 0) {
        const total = groupAccounts
          .filter((a) => !a.is_hidden)
          .reduce((sum, a) => {
            const acctCurrency = (a as any).currency || 'EUR';
            const rate = getRate(acctCurrency, currency) ?? 1;
            return sum + a.balance * rate;
          }, 0);

        groups.push({
          key: group.key,
          type: group.type,
          label: t(`accounts.group.${group.key}` as any),
          accounts: groupAccounts,
          total,
          change: 0, // Would need historical data
        });
      }
    }

    return groups;
  }, [accounts, t, currency, getRate]);

  // Converted summary stats derived from groupedAccounts
  const convertedStats = useMemo(() => {
    const totalAssets = groupedAccounts
      .filter((g) => g.type === 'asset')
      .reduce((sum, g) => sum + g.total, 0);
    const assetsByGroup: Record<string, number> = {};
    for (const g of groupedAccounts) {
      if (g.type === 'asset' && g.total !== 0) {
        assetsByGroup[g.key] = g.total;
      }
    }
    return { totalAssets, assetsByGroup };
  }, [groupedAccounts]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      localStorage.setItem('clevia-collapsed-groups', JSON.stringify([...next]));
      return next;
    });
  };

  const handleDragEnd = (groupKey: string) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const group = groupedAccounts.find((g) => g.key === groupKey);
    if (!group) return;

    const displayList = showHidden ? group.accounts : group.accounts.filter((a) => !a.is_hidden);
    const oldIndex = displayList.findIndex((a) => a.id === active.id);
    const newIndex = displayList.findIndex((a) => a.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(displayList, oldIndex, newIndex);
    reorderAccounts(newOrder.map((a) => a.id));
  };

  const handleDelete = (id: string) => {
    setOpenMenu(null);
    const account = accounts.find((a) => a.id === id);
    if (account) setArchiveTarget(account);
  };

  const handleConfirmArchive = async () => {
    if (!archiveTarget) return;
    await updateAccount(archiveTarget.id, {
      is_hidden: true,
      metadata: { ...(archiveTarget.metadata || {}), archived: true },
    });
    setArchiveTarget(null);
  };

  const handleOpenLink = (account: Account) => {
    setOpenMenu(null);
    setLinkTarget(account);
    setResumedConnection(null);
  };

  const handleDisconnect = async (account: Account) => {
    setOpenMenu(null);
    const description = (
      account.powens_linked_at
        ? t('accountLink.disconnectLinkedDesc')
        : t('accountLink.disconnectDesc')
    ).replace('{name}', account.name);

    const confirmed = await confirm({
      title: t('accountLink.disconnectTitle'),
      description,
      confirmLabel: t('accountLink.disconnectConfirm'),
      cancelLabel: language === 'fr' ? 'Annuler' : 'Cancel',
      // Losing data is destructive; a linked account keeps all of it.
      variant: account.powens_linked_at ? 'default' : 'destructive',
    });
    if (!confirmed) return;

    const result = await deleteAccount(account.id);
    if (result.success) {
      toast({ title: t('accountLink.disconnected') });
      await loadTransactions();
    } else {
      toast({
        title: t('accountLink.disconnectFailed'),
        description: result.error instanceof Error ? result.error.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (account: Account) => {
    navigate(`/accounts/${account.id}/edit`);
    setOpenMenu(null);
  };

  const handleTransfer = (fromAccountId?: string) => {
    setTransferFromAccountId(fromAccountId);
    setIsTransferModalOpen(true);
    setOpenMenu(null);
  };

  const handleTransferSuccess = async () => {
    await loadTransactions();
    await loadAccounts();
  };

  const handleDownloadExcel = async () => {
    const isFr = language === 'fr';
    await exportStyledExcel({
      sheetName: isFr ? 'Comptes' : 'Accounts',
      fileName: 'clevia-accounts.xlsx',
      columns: [
        { header: isFr ? 'Nom' : 'Name', key: 'name', width: 22 },
        { header: 'Type', key: 'type', width: 14 },
        { header: isFr ? 'Catégorie' : 'Category', key: 'category', width: 16 },
        { header: isFr ? 'Solde' : 'Balance', key: 'balance', width: 16, style: 'currency' },
        { header: 'Institution', key: 'institution', width: 18 },
        { header: isFr ? 'Créé le' : 'Created', key: 'created', width: 14, style: 'date' },
      ],
      data: accounts.map((a) => {
        const d = new Date(a.created_at);
        return {
          name: a.name,
          type: a.type,
          category: a.subtype,
          balance: a.balance,
          institution: a.institution || '',
          created: d.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
        };
      }),
      totals: {
        balance: accounts.reduce((sum, a) => sum + a.balance, 0),
      },
    });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const isFr = language === 'fr';
    const prefix = isFr ? 'il y a ' : '';
    const suffix = isFr ? '' : ' ago';

    if (diffHours < 24) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffMinutes < 1) return t('accounts.justNow');
      if (diffMinutes < 60) return `${prefix}${diffMinutes} min${suffix}`;
      return `${prefix}${Math.floor(diffHours)}h${suffix}`;
    }

    return date.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="accounts" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        <PageHeader
          title={t('accounts.title')}
          subtitle={t('accounts.subtitle')}
          action={
            <div className="flex items-center gap-3">
              {/* Powens refreshes a connection every 24h on its own, so without
                  this a payment made this morning can stay invisible until
                  tomorrow. Only shown when there is a bank to refresh. */}
              {hasPowensAccounts && (
                <button
                  onClick={() => refreshBanks()}
                  disabled={isRefreshingBanks}
                  title={t('powens.refreshBanks')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#16c47f] text-gray-700 dark:text-gray-200 font-semibold rounded-xl transition-all duration-200 disabled:cursor-wait disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshingBanks ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{t('powens.refreshBanks')}</span>
                </button>
              )}
              <button
                onClick={() => handleTransfer()}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <ArrowLeftRight className="w-4 h-4" />
                {t('accounts.transfer')}
              </button>
              <button
                onClick={() => navigate('/accounts/add-bank')}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <Plus className="w-4 h-4" />
                {t('accounts.addAccount')}
              </button>
            </div>
          }
        />

        <div className="p-6 space-y-6">
          {/* Net Worth Header */}
          <div
            id="guide-accounts"
            className="scroll-mt-24 rounded-2xl target:ring-2 target:ring-[#16c47f]/50 target:ring-offset-4 target:ring-offset-[#f9fafb] dark:target:ring-offset-[#0f1117] target:shadow-[0_0_0_8px_rgba(22,196,127,0.08)] bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 p-6 transition-shadow duration-500"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-6">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-widest">
                    {t('accounts.netWorth')}
                  </span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-4xl font-bold text-gray-900 dark:text-white">
                    {formatAmount(convertedNetWorth ? convertedNetWorth.net_worth : stats.netWorth)}
                  </span>
                </div>
                {convertedNetWorth?.rates_updated_at && (
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                    {language === 'fr'
                      ? `Conversion basée sur les taux du ${new Date(convertedNetWorth.rates_updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })} à ${new Date(convertedNetWorth.rates_updated_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                      : `Conversion based on rates from ${new Date(convertedNetWorth.rates_updated_at).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit' })} at ${new Date(convertedNetWorth.rates_updated_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                )}
                {periodVariation && !isSecretMode && (
                  <div className="mt-2 flex flex-col">
                    <span
                      className={`text-lg font-semibold ${
                        periodVariation.diff > 0
                          ? 'text-emerald-500'
                          : periodVariation.diff < 0
                            ? 'text-red-500'
                            : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {periodVariation.diff > 0 ? '+' : ''}
                      {formatAmount(periodVariation.diff)}
                    </span>
                    <span
                      className={`text-xs ${
                        periodVariation.diff > 0
                          ? 'text-emerald-500/80'
                          : periodVariation.diff < 0
                            ? 'text-red-500/80'
                            : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {periodVariation.pct !== null && (
                        <>
                          ({periodVariation.diff > 0 ? '+' : ''}
                          {periodVariation.pct.toFixed(1)}
                          {'\u00A0'}%){' '}
                        </>
                      )}
                      {t('accounts.vsPreviousPeriod' as any)}
                    </span>
                  </div>
                )}
              </div>

              {/* Period selector */}
              <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-700 rounded-xl p-1">
                {NET_WORTH_PERIODS.map((key) => (
                  <button
                    key={key}
                    onClick={() => setPeriod(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      period === key
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t(`accounts.period.${key}` as any)}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart */}
            {accounts.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="netWorthGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#16c47f" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#16c47f" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(d: string) => {
                      const date = new Date(d + 'T12:00:00Z');
                      const days = period === 'all' ? null : NET_WORTH_PERIOD_DAYS[period];
                      if (days !== null && days <= 30)
                        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                          day: 'numeric',
                          month: 'short',
                        });
                      return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                        month: 'short',
                        year: days === null || days > 365 ? '2-digit' : undefined,
                      });
                    }}
                    interval="preserveStartEnd"
                    minTickGap={40}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    tickFormatter={(v: number) => (isSecretMode ? '•••' : formatCompact(v))}
                    width={70}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload?.[0]) return null;
                      const date = new Date(label + 'T12:00:00Z');
                      const dateStr = date.toLocaleDateString(
                        language === 'fr' ? 'fr-FR' : 'en-US',
                        {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }
                      );
                      return (
                        <div
                          style={{
                            background: 'rgba(17, 24, 39, 0.95)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: '14px',
                            padding: '14px 18px',
                            boxShadow:
                              '0 20px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06)',
                            minWidth: '160px',
                          }}
                        >
                          <div style={{ color: '#9ca3af', fontSize: '12px', marginBottom: '8px' }}>
                            {dateStr}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div
                              style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                background: '#16c47f',
                                boxShadow: '0 0 8px #16c47f66',
                                flexShrink: 0,
                              }}
                            />
                            <span style={{ color: '#e5e7eb', fontSize: '12px' }}>
                              {t('accounts.netWorth')}
                            </span>
                          </div>
                          <div
                            style={{
                              color: '#fff',
                              fontSize: '18px',
                              fontWeight: 700,
                              letterSpacing: '-0.02em',
                              marginTop: '4px',
                              paddingLeft: '18px',
                            }}
                          >
                            {isSecretMode ? '•••' : formatAmount(payload[0].value as number)}
                          </div>
                        </div>
                      );
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#16c47f"
                    strokeWidth={2}
                    fill="url(#netWorthGradient)"
                    dot={false}
                    activeDot={{ r: 5, fill: '#16c47f', strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center">
                <div className="text-center">
                  <Wallet className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 text-sm">{t('accounts.empty')}</p>
                  <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                    {t('accounts.emptyDesc')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Content: Account Groups + Summary */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Account Groups - Left side (2/3) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Banks whose data Powens is still fetching. Shown above the
                  groups, and outside the loading/empty branches, so a user who
                  has just linked a bank sees it straight away instead of an
                  empty page that reads as a failed connection. */}
              {pendingConnections.map((connection) => {
                const blocked = Boolean(connection.state);
                return (
                  <div
                    key={connection.id}
                    className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5 flex items-center gap-4"
                  >
                    <div
                      className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        blocked
                          ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                          : 'bg-gradient-to-br from-sky-400 to-blue-600'
                      }`}
                    >
                      {blocked ? (
                        <ShieldAlert className="w-6 h-6 text-white" />
                      ) : (
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {connection.connector_name || t('powens.connectBank')}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {blocked ? t('powens.actionRequiredDesc') : t('powens.importingDesc')}
                      </p>
                    </div>

                    {blocked ? (
                      <button
                        onClick={() => connectPowens(connection.powens_connection_id)}
                        disabled={isPowensConnecting}
                        className="px-4 py-2 text-sm font-semibold rounded-xl bg-amber-500 hover:bg-amber-600 text-white transition-colors disabled:opacity-60"
                      >
                        {t('powens.reconnect')}
                      </button>
                    ) : (
                      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        {t('powens.importing')}
                      </span>
                    )}
                  </div>
                );
              })}

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6 animate-pulse"
                    >
                      <div className="h-6 w-40 bg-gray-100 dark:bg-gray-700 rounded mb-4" />
                      <div className="space-y-3">
                        <div className="h-14 bg-gray-50 dark:bg-gray-700 rounded-xl" />
                        <div className="h-14 bg-gray-50 dark:bg-gray-700 rounded-xl" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : groupedAccounts.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-12">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-[#16c47f]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Wallet className="w-8 h-8 text-[#16c47f]" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {t('accounts.empty')}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
                      {t('accounts.emptyDesc')}
                    </p>
                    <button
                      onClick={() => navigate('/accounts/add-bank')}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all duration-200 shadow-sm"
                    >
                      <Plus className="w-4 h-4" />
                      {t('accounts.addAccount')}
                    </button>
                  </div>
                </div>
              ) : (
                groupedAccounts.map((group) => {
                  const isCollapsed = collapsedGroups.has(group.key);
                  const visibleAccounts = group.accounts.filter((a) => !a.is_hidden);
                  const hiddenAccounts = group.accounts.filter((a) => a.is_hidden);
                  const displayAccounts = showHidden ? group.accounts : visibleAccounts;

                  return (
                    <div
                      key={group.key}
                      className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 overflow-hidden"
                    >
                      {/* Group header */}
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-t-2xl"
                      >
                        <div className="flex items-center gap-3">
                          {isCollapsed ? (
                            <ChevronRight className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          )}
                          <span className="text-gray-900 dark:text-white font-semibold">
                            {group.label}
                          </span>
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            {visibleAccounts.length}{' '}
                            {visibleAccounts.length === 1 ? 'account' : 'accounts'}
                          </span>
                        </div>
                        <span
                          className={`font-bold ${groupDisplayTotal(group) < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}
                        >
                          {formatAmount(groupDisplayTotal(group))}
                        </span>
                      </button>

                      {/* Account items */}
                      {!isCollapsed && (
                        <div className="border-t border-gray-100 dark:border-gray-700">
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd(group.key)}
                          >
                            <SortableContext
                              items={displayAccounts.map((a) => a.id)}
                              strategy={verticalListSortingStrategy}
                            >
                              {displayAccounts.map((account, index) => {
                                const Icon = SUBTYPE_ICONS[account.subtype] || DollarSign;
                                const gradientColor =
                                  SUBTYPE_COLORS[account.subtype] || 'from-gray-500 to-slate-500';
                                const isMenuOpen = openMenu === account.id;
                                const matchedBank = findBankByName(account.institution);

                                return (
                                  <SortableAccountWrapper key={account.id} id={account.id}>
                                    {({ dragHandleProps }) => (
                                      <div
                                        onClick={() => {
                                          if (account.subtype !== 'cash') {
                                            navigate(`/accounts/${account.id}`);
                                          }
                                        }}
                                        className={`flex items-center justify-between p-4 px-5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${account.subtype !== 'cash' ? 'cursor-pointer' : ''} ${index > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''} ${account.is_hidden ? 'opacity-50' : ''}`}
                                      >
                                        <div className="flex items-center gap-4 min-w-0">
                                          <div
                                            {...dragHandleProps}
                                            onClick={(e) => e.stopPropagation()}
                                            className="cursor-grab active:cursor-grabbing touch-none text-gray-300 dark:text-gray-600 hover:text-gray-400 dark:hover:text-gray-500"
                                          >
                                            <GripVertical className="w-4 h-4" />
                                          </div>
                                          {matchedBank ? (
                                            <div className="w-10 h-10 shrink-0 rounded-xl bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex items-center justify-center shadow-sm overflow-hidden">
                                              <img
                                                src={getBankLogoUrl(matchedBank)}
                                                alt={matchedBank.name}
                                                className="w-7 h-7 object-contain"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = 'none';
                                                }}
                                              />
                                            </div>
                                          ) : account.subtype === 'crypto' &&
                                            (account.metadata as Record<string, unknown>)?.thumb ? (
                                            <div
                                              className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${gradientColor} flex items-center justify-center shadow-sm overflow-hidden`}
                                            >
                                              <img
                                                src={
                                                  (account.metadata as Record<string, unknown>)
                                                    .thumb as string
                                                }
                                                alt={account.name}
                                                className="w-7 h-7 rounded-full"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = 'none';
                                                }}
                                              />
                                            </div>
                                          ) : (
                                            <div
                                              className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${gradientColor} flex items-center justify-center shadow-sm`}
                                            >
                                              <Icon className="w-5 h-5 text-white" />
                                            </div>
                                          )}
                                          <div className="min-w-0">
                                            <p className="flex items-center gap-2 text-gray-900 dark:text-white font-medium text-sm">
                                              <span className="truncate">{account.name}</span>
                                              <AccountSourceBadge source={account.source} />
                                            </p>
                                            <p className="truncate text-gray-400 dark:text-gray-500 text-xs">
                                              {t(`accounts.subtype.${account.subtype}` as any)}
                                              {account.institution && ` • ${account.institution}`}
                                              {account.interest_rate != null &&
                                                ` • ${account.interest_rate}%`}
                                            </p>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-3">
                                          <div className="text-right">
                                            <p
                                              className={`font-semibold text-sm ${displayBalance(account) < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}
                                            >
                                              {formatAmountInCurrency(
                                                displayBalance(account),
                                                account.currency || 'EUR'
                                              )}
                                            </p>
                                            <p className="text-gray-400 dark:text-gray-500 text-xs">
                                              {formatDate(account.updated_at)}
                                            </p>
                                          </div>

                                          {/* Actions menu */}
                                          <div className="relative">
                                            <button
                                              data-menu-id={account.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (isMenuOpen) {
                                                  setOpenMenu(null);
                                                } else {
                                                  const rect =
                                                    e.currentTarget.getBoundingClientRect();
                                                  const menuHeight = 270; // approximate menu height
                                                  const spaceBelow =
                                                    window.innerHeight - rect.bottom;
                                                  const top =
                                                    spaceBelow < menuHeight
                                                      ? Math.max(8, rect.top - menuHeight - 4)
                                                      : rect.bottom + 4;
                                                  setMenuPos({
                                                    top,
                                                    right: window.innerWidth - rect.right,
                                                  });
                                                  setOpenMenu(account.id);
                                                }
                                              }}
                                              className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                                            >
                                              <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                                            </button>

                                            {isMenuOpen &&
                                              createPortal(
                                                <>
                                                  <div
                                                    className="fixed inset-0 z-[9998]"
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      e.preventDefault();
                                                      setOpenMenu(null);
                                                    }}
                                                  />
                                                  <div
                                                    className="fixed z-[9999] w-48 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl shadow-lg py-1"
                                                    style={{
                                                      top: menuPos.top,
                                                      right: menuPos.right,
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTransfer(account.id);
                                                      }}
                                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                                                    >
                                                      <ArrowLeftRight className="w-3.5 h-3.5" />
                                                      {t('accounts.transferFrom')}
                                                    </button>
                                                    <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEdit(account);
                                                      }}
                                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                    >
                                                      <Pencil className="w-3.5 h-3.5" />
                                                      {t('accounts.edit')}
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleHidden(account.id, account.is_hidden);
                                                        setOpenMenu(null);
                                                      }}
                                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                    >
                                                      {account.is_hidden ? (
                                                        <>
                                                          <Eye className="w-3.5 h-3.5" />
                                                          {t('accounts.show')}
                                                        </>
                                                      ) : (
                                                        <>
                                                          <EyeOff className="w-3.5 h-3.5" />
                                                          {t('accounts.hide')}
                                                        </>
                                                      )}
                                                    </button>
                                                    {isLinkableToBank(account) && (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleOpenLink(account);
                                                        }}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                      >
                                                        <Link2 className="w-3.5 h-3.5" />
                                                        {t('accountLink.menu')}
                                                      </button>
                                                    )}
                                                    {account.source === 'powens' && (
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleDisconnect(account);
                                                        }}
                                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                                      >
                                                        <Unlink className="w-3.5 h-3.5" />
                                                        {t('accountLink.disconnect')}
                                                      </button>
                                                    )}
                                                    <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(account.id);
                                                      }}
                                                      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                                    >
                                                      <Trash2 className="w-3.5 h-3.5" />
                                                      {t('accounts.delete')}
                                                    </button>
                                                  </div>
                                                </>,
                                                document.body
                                              )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </SortableAccountWrapper>
                                );
                              })}
                            </SortableContext>
                          </DndContext>

                          {/* Hidden accounts indicator */}
                          {!showHidden && hiddenAccounts.length > 0 && (
                            <button
                              onClick={() => setShowHidden(true)}
                              className="w-full flex items-center gap-2 p-3 px-5 text-gray-400 dark:text-gray-500 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors border-t border-gray-100 dark:border-gray-700"
                            >
                              <EyeOff className="w-3.5 h-3.5" />
                              {hiddenAccounts.length}{' '}
                              {hiddenAccounts.length === 1
                                ? t('accounts.hidden')
                                : t('accounts.hidden_plural')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Summary Panel - Right side (1/3) */}
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-6">
                {/* Summary Header */}
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-gray-900 dark:text-white font-semibold">
                    {t('accounts.summary')}
                  </h3>
                  <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                    <button
                      onClick={() => setSummaryView('totals')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        summaryView === 'totals'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {t('accounts.summary.totals')}
                    </button>
                    <button
                      onClick={() => setSummaryView('percent')}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        summaryView === 'percent'
                          ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      {t('accounts.summary.percent')}
                    </button>
                  </div>
                </div>

                {/* Assets Section */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-900 dark:text-white font-semibold text-sm">
                      {t('accounts.summary.assets')}
                    </span>
                    <span className="text-gray-900 dark:text-white font-bold">
                      {formatAmount(convertedStats.totalAssets)}
                    </span>
                  </div>

                  {/* Asset breakdown */}
                  {Object.entries(convertedStats.assetsByGroup).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm pl-2">
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${GROUP_DOT_COLORS[key] || 'bg-gray-300 dark:bg-gray-600'}`}
                        />
                        <span className="text-gray-500 dark:text-gray-400">
                          {t(`accounts.group.${key}` as any)}
                        </span>
                      </div>
                      <span className="text-gray-700 dark:text-gray-300">
                        {summaryView === 'totals'
                          ? formatAmount(value)
                          : `${convertedStats.totalAssets > 0 ? ((value / convertedStats.totalAssets) * 100).toFixed(1) : '0'}%`}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Download Excel */}
                {accounts.length > 0 && (
                  <button
                    onClick={handleDownloadExcel}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[#16c47f] hover:text-[#12b070] hover:bg-[#16c47f]/5 dark:hover:bg-[#16c47f]/10 rounded-xl transition-all text-sm font-medium"
                  >
                    <Download className="w-4 h-4" />
                    {t('accounts.summary.downloadExcel')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Transfer Modal */}
        <TransferModal
          isOpen={isTransferModalOpen}
          onClose={() => {
            setIsTransferModalOpen(false);
            setTransferFromAccountId(undefined);
          }}
          onSuccess={handleTransferSuccess}
          defaultFromAccountId={transferFromAccountId}
        />

        <LinkBankAccountModal
          key={linkTarget?.id ?? 'closed'}
          account={linkTarget}
          accounts={accounts}
          connectionId={linkConnectionId}
          hasReturnedFromBank={resumedConnection !== null}
          isBankSyncing={
            resumedConnection !== null &&
            linkBankAccounts.length === 0 &&
            (linkConnectionId === null || pendingConnections.some((c) => !c.state))
          }
          isConnecting={isPowensConnecting}
          onConnectBank={(account) => {
            saveLinkIntent(account.id);
            connectPowens();
          }}
          onClose={() => {
            setLinkTarget(null);
            setResumedConnection(null);
            clearLinkIntent();
          }}
          onLinked={async (summary) => {
            const linkedName = linkTarget?.name ?? summary.bank_account_name;
            setLinkTarget(null);
            setResumedConnection(null);
            clearLinkIntent();
            toast({
              title: t('accountLink.successTitle'),
              description: t('accountLink.successDesc').replace('{name}', linkedName),
            });
            await Promise.all([loadAccounts(), loadTransactions()]);
          }}
        />

        {/* Archive Confirmation Modal */}
        {archiveTarget &&
          createPortal(
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => setArchiveTarget(null)}
              />
              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-6 pb-4 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {language === 'fr' ? 'Supprimer ce compte ?' : 'Delete this account?'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {language === 'fr'
                        ? `Le compte "${archiveTarget.name}" sera archivé et masqué de votre vue principale. Il ne sera pas supprimé définitivement.`
                        : `The account "${archiveTarget.name}" will be archived and hidden from your main view. It will not be permanently deleted.`}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5">
                      {language === 'fr'
                        ? 'Vous pourrez le réactiver à tout moment depuis "Gestion des comptes" dans les paramètres.'
                        : 'You can reactivate it anytime from "Account Management" in settings.'}
                    </p>
                  </div>
                  <button
                    onClick={() => setArchiveTarget(null)}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                {/* Stats block */}
                {(archiveStats.transactions > 0 || archiveStats.transfers > 0) && (
                  <div className="mx-6 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-xl">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {language === 'fr' ? 'Ce compte contient :' : 'This account contains:'}
                    </p>
                    <div className="space-y-1">
                      {archiveStats.transactions > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          {archiveStats.transactions}{' '}
                          {language === 'fr' ? 'transactions' : 'transactions'}
                        </p>
                      )}
                      {archiveStats.transfers > 0 && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                          {archiveStats.transfers} {language === 'fr' ? 'transferts' : 'transfers'}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Reassurance */}
                <div className="mx-6 mt-3 flex items-start gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                  <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                    <p>
                      {language === 'fr'
                        ? "L'historique des transactions restera intact."
                        : 'Transaction history will remain intact.'}
                    </p>
                    <p>
                      {language === 'fr'
                        ? 'Les statistiques passées ne seront pas supprimées.'
                        : 'Past statistics will not be deleted.'}
                    </p>
                    <p>
                      {language === 'fr'
                        ? 'Le nom du compte restera visible dans les transactions historiques.'
                        : 'The account name will remain visible in historical transactions.'}
                    </p>
                  </div>
                </div>

                {/* Buttons */}
                <div className="px-6 py-4 mt-2 flex gap-3">
                  <button
                    onClick={() => setArchiveTarget(null)}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-colors"
                  >
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </button>
                  <button
                    onClick={handleConfirmArchive}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors"
                  >
                    {language === 'fr' ? 'Supprimer le compte' : 'Delete account'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}
      </main>
    </div>
  );
}
